import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import {PrimaryExperimentalRoleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentalRole} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {getRoleSymbol} from 'samples/experimental-roles/ExperimentalRoleUtil';
import PrimaryExperimentalRoleSymbol from 'samples/experimental-roles/PrimaryExperimentalRoleSymbol';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_POWDER_LIGHT, GRAY} from 'ui-library/Colors';

// Always show at least 3 buttons for group options.
const MINIMUM_GROUP_OPTIONS = 3;

/**
 * A list of numbered buttons to assign groups for the given role and subrole to wells.
 */
export default function WellRoleGroupButtons({
  roleGroups,
  primaryRole,
  subrole,
  onAssignGroup,
}: {
  primaryRole: PrimaryExperimentalRoleType;
  subrole: string | null;
  onAssignGroup: (role: ExperimentalRole) => void;
  roleGroups: Array<ExperimentalRole>;
}): JSX.Element {
  const [numGroups, setNumGroups] = useState(Math.max(roleGroups.length, MINIMUM_GROUP_OPTIONS));

  useEffect(() => {
    setNumGroups(Math.max(numGroups, roleGroups.length, MINIMUM_GROUP_OPTIONS));
  }, [roleGroups, numGroups]);

  function getGroupOptions(): Array<ExperimentalRole> {
    return [...Array(numGroups)].map((_, index) => ({
      primaryRole,
      subrole,
      group: index + 1,
    }));
  }

  function addGroupOption(): void {
    setNumGroups(numGroups + 1);
  }

  return (
    <div className={css(styles.groupsContainer)}>
      {getGroupOptions().map((groupOption, index) => (
        <span
          key={index}
          className={classNames(css(styles.roleOption), css(styles.groupOption))}
          onClick={() => {
            onAssignGroup(groupOption);
          }}
          {...getTestProps({
            component: 'WellRoleAssignmentSettings',
            element: 'WellRoleGroup',
            key: groupOption.group.toString(),
          })}
        >
          <PrimaryExperimentalRoleSymbol
            radius={10}
            primaryRole={groupOption.primaryRole}
            label={getRoleSymbol(groupOption)}
          />
        </span>
      ))}
      <Button
        className={css(styles.groupAddButton)}
        icon="compose"
        size={ButtonSize.SMALL}
        buttonStyle={ButtonStyle.TERTIARY_WHITE}
        onClick={addGroupOption}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  roleOption: {
    borderRadius: 4,
    cursor: 'pointer',
    textAlign: 'center',
    border: `1px solid ${GRAY}`,
    ':hover': {
      backgroundColor: BLUE_POWDER_LIGHT,
    },
  },
  groupsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  groupOption: {
    display: 'flex',
    height: 28,
    padding: 3,
    margin: '4px 8px 4px 0',
  },
  groupAddButton: {
    margin: '4px 0',
  },
});
